import { createWebAnalytics, WebAnalyticsModule } from '@agoda/analytics-data-acquisition';
import { IMessagingClient } from '@agoda/messaging-client';
import { WebAnalyticsBinder, IWebAnalyticsService } from './types';

import {
    UniversalLoginAppDataContextIdsV13,
    UniversalLoginAppDataContextFieldsV13,
} from '@agoda/analytics-messages-definition-universal-login/applications.split';
import {
    analyticsSessionExpiry,
    analyticsSessionId,
    analyticsSessionSignature,
    iframeParentHost,
    isIframe,
    pageVisitId,
    referrerUrlFunc,
    requestUrlFunc,
    screenHeight,
    screenWidth,
} from '@agoda/analytics-data-acquisition/dist/functions/data';
import { AppContextData } from '@contexts/app/AppContextData';
import { Member } from '@contexts/member/MemberContext';

// DONT modify this AppType this should be same as in analytics library
type AppType =
    | 'desk-web'
    | 'mob-web'
    | 'mspa'
    | 'ios'
    | 'android'
    | 'wechat-mp'
    | 'ycs-desk-web'
    | 'host-manage-web'
    | 'ycs-app-ios'
    | 'ycs-app-android'
    | 'engage-desk-web'
    | 'athena';

export class WebAnalyticsService implements IWebAnalyticsService {
    private readonly messagingClient: IMessagingClient;
    private readonly webAnalyticsModule: WebAnalyticsModule;

    constructor(messagingClient: IMessagingClient) {
        this.messagingClient = messagingClient;
        this.webAnalyticsModule = createWebAnalytics(this.messagingClient);
    }

    // bind to be called, to intiate the webAnalytics setup
    public bind = (
        binder: WebAnalyticsBinder,
        appContext: AppContextData,
        member: Member | null,
        externalSessionId?: string
    ): void => {
        const isLoggedIn = member && !!member.id ? 1 : 0;
        this.setAnalyticsAppContext(this.webAnalyticsModule, appContext, member, externalSessionId);

        binder(this.webAnalyticsModule, isLoggedIn);
    };

    public getAnalyticsAppType(appId: String, wlAppDeviceType?: String | null) {
        // changes covered under JTBAPP-472, ul-cronos
        if (wlAppDeviceType) return wlAppDeviceType;

        switch (appId.toLowerCase()) {
            case 'mspa': {
                return 'mspa';
            }
            case 'androidagoda': {
                return 'android';
            }
            case 'iosagoda': {
                return 'ios';
            }
            case 'reactnativeagoda': {
                return 'reactnative';
            }
            case 'ycs': {
                return 'ycs-desk-web';
            }
            case 'cupid':
            default: {
                return 'desk-web';
            }
        }
    }

    private setAnalyticsAppContext(
        webAnalyticsModule: WebAnalyticsModule,
        appContext: AppContextData,
        member: Member | null,
        externalSessionId?: string
    ) {
        const AnalyticsSessionExpiry = () => analyticsSessionExpiry();
        const AnalyticsSessionId = () => externalSessionId ?? analyticsSessionId();
        const AnalyticsSessionSignature = () => analyticsSessionSignature();
        const ChannelId = () => appContext.cid;
        const Currency = () => appContext.currency.toLowerCase() as any; // 'usd'
        const IframeParentHost = () => iframeParentHost();
        const IsIframe = () => isIframe();
        const IsIncognito = () => false;
        const Language = () => appContext.cultureCode as any; // 'en-us'
        const LoggedIn = member && !!member.id ? 1 : 0;
        const MemberId = (member && member.id) || 0;
        const PageVisitId = pageVisitId();
        const ReferrerUrl = () => referrerUrlFunc();
        const RequestUrl = () => requestUrlFunc();
        const ScreenHeight = () => screenHeight();
        const ScreenWidth = () => screenWidth();
        const SessionId = () => appContext.sessionId;
        const UserId = appContext.userId;
        const WebServerName = appContext.machineName;
        const WhitelabelId = appContext.whitelabelId;
        const IsWebView = appContext.isWebView;
        const wlAppDeviceType = appContext.wlAppDeviceType;

        webAnalyticsModule.appContext<UniversalLoginAppDataContextIdsV13, UniversalLoginAppDataContextFieldsV13>(
            {
                app_data: 'universal-login',
                app_data_version: 13,
            },
            {
                app_type: this.getAnalyticsAppType(appContext.appId, wlAppDeviceType) as AppType,
                analytics_session_id: AnalyticsSessionId,
                analytics_session_expiry: AnalyticsSessionExpiry,
                analytics_session_signature: AnalyticsSessionSignature,
                channel_id: ChannelId,
                currency: Currency,
                iframe_parent_host: IframeParentHost,
                is_iframe: IsIframe,
                is_incognito: IsIncognito,
                is_webview: IsWebView,
                language: Language,
                logged_in: LoggedIn,
                member_id: MemberId,
                page_visit_id: PageVisitId,
                referrer_url: ReferrerUrl(),
                request_url: RequestUrl(),
                screen_height: ScreenHeight,
                screen_width: ScreenWidth,
                session_id: SessionId,
                user_id: UserId,
                web_server_name: WebServerName,
                whitelabelid: WhitelabelId,
            }
        );
    }

    public getModule = () => {
        return this.webAnalyticsModule;
    };
}
